import React, { useState, useEffect } from "react";
import { FaTrashAlt, FaEdit, FaPlusCircle, FaTimes } from "react-icons/fa";
import { createCompany, updateCompany, deleteCompany, fetchCompanies } from "../../utils/company";

const Subscription = () => {
    const [companies, setCompanies] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingCompany, setEditingCompany] = useState(null);
    const [newCompany, setNewCompany] = useState({ companyName: "", email: "" });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [saveError, setSaveError] = useState(null);

    useEffect(() => {
        const loadCompanies = async () => {
            try {
                const data = await fetchCompanies();
                setCompanies(data.data);
            } catch (err) {
                setError("Failed to fetch companies. Please try again later.");
            } finally {
                setLoading(false);
            }
        };
        loadCompanies();
    }, []);

    const PaginationButton = ({ direction, disabled, onClick }) => (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`px-3 py-1 rounded ${disabled
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
            aria-label={`Go to ${direction} page`}
        >
            {direction === "prev" ? "<" : ">"}
        </button>
    );

    const handleDelete = async (id) => {
        try {
            setLoading(true);
            await deleteCompany(id);
            setCompanies(companies.filter((company) => company.id !== id));
        } catch (err) {
            setError("Failed to delete company. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveCompany = async () => {
        setSaving(true);
        setSaveError(null);
        try {
            if (editingCompany) {
                const updatedCompany = await updateCompany(editingCompany.id, newCompany);
                setCompanies(companies.map((c) => (c.id === editingCompany.id ? newCompany : c)));
            } else {
                const savedCompany = await createCompany(newCompany);
                setCompanies([...companies, newCompany]);
            }
            setShowModal(false);
            setNewCompany({ companyName: "", email: "" });
            setEditingCompany(null);
        } catch (error) {
            setSaveError("Failed to save company. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const totalPages = Math.ceil(companies.length / itemsPerPage);




    const handlePageChange = (direction) => {
        setCurrentPage((prev) => {
            if (direction === "next" && prev < totalPages) return prev + 1;
            if (direction === "prev" && prev > 1) return prev - 1;
            return prev;
        });
    };

    const openEditModal = (company) => {
        setEditingCompany(company);
        setNewCompany({ companyName: company.companyName, email: company.email });
        setShowModal(true);
    };

    if (loading) {
        return <p className="text-center text-gray-500 mt-6">Loading companies...</p>;
    }


    return (
        <div>
            <h1 className="text-3xl mt-5 text-center font-bold text-blue-600">Companies Management</h1>
            <div className="flex items-center mt-[30px] bg-gray-100">
                <div className="p-5 bg-white shadow-lg rounded-lg w-full">
                    <div className="flex justify-between items-center mb-5">
                        <h1 className="text-2xl font-bold">Company</h1>
                        <button onClick={() => setShowModal(true)} className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center">
                            <FaPlusCircle className="mr-2" /> Add Company
                        </button>
                    </div>

                    <table className="w-full border-collapse border border-gray-300">
                        <thead className="bg-blue-100">
                            <tr>
                                <th className="border border-gray-300 px-4 py-2">ID</th>
                                <th className="border border-gray-300 px-4 py-2">Name</th>
                                <th className="border border-gray-300 px-4 py-2">Email</th>
                                <th className="border border-gray-300 px-4 py-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {companies.map((company) => (
                                <tr key={company.id} className="even:bg-gray-50">
                                    <td className="border border-gray-300 px-4 py-2">{company.id}</td>
                                    <td className="border border-gray-300 px-4 py-2">{company.companyName}</td>
                                    <td className="border border-gray-300 px-4 py-2">{company.email}</td>
                                    <td className="border border-gray-300 px-4 py-2 text-center">
                                        <button onClick={() => handleDelete(company.id)} className="text-red-500 hover:text-red-700 mx-2">
                                            <FaTrashAlt />
                                        </button>
                                        <button onClick={() => openEditModal(company)} className="text-blue-500 hover:text-blue-700 mx-2">
                                            <FaEdit />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                {/* Pagination */}
                <div className="flex justify-between items-center mt-5">
                    <PaginationButton
                        direction="prev"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange("prev")}
                    />
                    <span className="text-gray-700 font-medium">
                        Page {currentPage} of {totalPages}
                    </span>
                    <PaginationButton
                        direction="next"
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange("next")}
                    />
                </div>
                </div>

                {showModal && (
                    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center">
                        <div className="bg-white p-6 rounded-lg shadow-lg w-[90%] max-w-[400px]">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-bold text-blue-600">{editingCompany ? "Edit Company" : "Add Company"}</h2>
                                <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                                    <FaTimes />
                                </button>
                            </div>
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSaveCompany();
                                }}
                            >
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-1">Company Name</label>
                                    <input type="text" className="w-full px-3 py-2 border rounded" value={newCompany.companyName} onChange={(e) => setNewCompany({ ...newCompany, companyName: e.target.value })} required />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-1">Email</label>
                                    <input type="email" className="w-full px-3 py-2 border rounded" value={newCompany.email} onChange={(e) => setNewCompany({ ...newCompany, email: e.target.value })} required />
                                </div>
                                {saving && <p className="text-center text-gray-500 mb-4">Saving...</p>}
                                {saveError && <p className="text-center text-red-500 mb-4">{saveError}</p>}
                                <div className="flex justify-end space-x-4">
                                    <button type="button" className="bg-gray-300 px-4 py-2 rounded" onClick={() => setShowModal(false)}>Cancel</button>
                                    <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded" disabled={saving}>{editingCompany ? "Update" : "Add"}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Subscription;
